import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"
import { isIOSWebView } from "../lib/ios_focus_guard"

const log = createLogger('KeyboardOffset')

// Visual Viewport 기반 키보드 높이 감지 임계값 (주소창 변화 무시)
const KEYBOARD_THRESHOLD_PX = 50

export default class extends Controller {
  static values = {
    duration: { type: Number, default: 150 }
  }

  connect() {
    // iOS WebView: WKWebView가 자동으로 visual viewport를 축소하여
    // fixed bottom-0 요소를 키보드 위로 이동시킴
    // 따라서 JS offset을 추가 적용하면 이중 이동이 발생하므로 비활성화
    if (isIOSWebView()) {
      log.debug('iOS WebView detected - keyboard offset disabled (WKWebView handles positioning)')
      return
    }

    this.isKeyboardVisible = false
    this.currentOffset = 0
    this.originalTransform = this.element.style.transform || ''
    this.androidKeyboardHeight = 0

    // Android Native 키보드 이벤트 리스너 (adjustNothing 모드에서 visualViewport 대신 사용)
    this.handleAndroidKeyboard = this.onAndroidKeyboardChange.bind(this)
    window.addEventListener('android:keyboardChange', this.handleAndroidKeyboard)

    // Web fallback: Visual Viewport API
    // Android WebView에서는 네이티브 이벤트가 우선되므로 visualViewport는 건너뜀
    if (window.visualViewport) {
      this.handleResize = this.handleResize.bind(this)
      window.visualViewport.addEventListener('resize', this.handleResize)
    }

    this.element.style.transition = `transform ${this.durationValue}ms ease-out`
    log.debug('KeyboardOffset connected', { hasVisualViewport: !!window.visualViewport })
  }

  disconnect() {
    // iOS WebView에서는 connect()에서 early return하므로 리스너가 없음
    if (isIOSWebView()) return

    window.removeEventListener('android:keyboardChange', this.handleAndroidKeyboard)
    if (window.visualViewport && this.handleResize) {
      window.visualViewport.removeEventListener('resize', this.handleResize)
    }
    this.element.style.transform = this.originalTransform
    this.element.style.transition = ''
  }

  /**
   * Android Native 키보드 이벤트 핸들러
   * Toas2tWebView.kt에서 IME 높이 변경 시 dispatch됨
   */
  onAndroidKeyboardChange(event) {
    const { height, visible } = event.detail
    this.androidKeyboardHeight = height

    log.debug('Android keyboard event', { height, visible })

    if (visible && height > 0) {
      this.showKeyboardOffset(height)
    } else {
      this.hideKeyboardOffset()
    }
  }

  /**
   * Visual Viewport resize 핸들러 (Web fallback)
   * Android WebView에서는 네이티브 이벤트가 우선
   * iOS WebView에서는 connect()에서 비활성화되므로 호출되지 않음
   */
  handleResize() {
    // Android WebView: 네이티브 이벤트가 처리했으면 visualViewport 무시
    if (this.androidKeyboardHeight > 0) return

    // Web fallback: visualViewport API 사용
    const keyboardHeight = window.innerHeight - window.visualViewport.height

    if (keyboardHeight > KEYBOARD_THRESHOLD_PX) {
      this.showKeyboardOffset(keyboardHeight)
    } else {
      this.hideKeyboardOffset()
    }
  }

  showKeyboardOffset(keyboardHeight) {
    const safeAreaBottom = this.getSafeAreaBottom()
    const offset = Math.max(0, keyboardHeight - safeAreaBottom)

    // Only update if offset changed (prevents unnecessary reflow)
    // This also handles emoji keyboard height changes correctly
    if (this.isKeyboardVisible && this.currentOffset === offset) return

    this.isKeyboardVisible = true
    this.currentOffset = offset
    this.element.style.transform = `translateY(-${offset}px)`
    log.debug(`Keyboard shown: ${keyboardHeight}px, offset: ${offset}px`)
  }

  hideKeyboardOffset() {
    if (!this.isKeyboardVisible) return

    this.isKeyboardVisible = false
    this.currentOffset = 0
    this.element.style.transform = this.originalTransform || 'translateY(0)'
    log.debug('Keyboard hidden')
  }

  getSafeAreaBottom() {
    const style = getComputedStyle(document.documentElement)
    // Android WebView: 네이티브 주입값 우선, CSS fallback
    // Note: iOS WebView는 connect()에서 early return하므로 이 함수가 호출되지 않음
    const safeAreaStr = style.getPropertyValue('--android-safe-area-bottom') ||
                        style.getPropertyValue('--safe-area-bottom') ||
                        '0px'
    return parseInt(safeAreaStr, 10) || 0
  }
}
